import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { apiClient } from "../lib/api";
import { BookmarkPlus, Trash2, ArrowRight, Bell } from "lucide-react";

export default function SavedSearchesPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const nav = useNavigate();

  const load = async () => {
    const { data } = await apiClient.get("/saved-searches");
    setItems(data.items || []);
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (user === undefined) return <div className="p-10 text-[#6b6b6b]">{t("common.loading")}</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/saved-searches" }} replace />;

  const remove = async (id) => {
    await apiClient.delete(`/saved-searches/${id}`);
    await load();
  };

  const run = (s) => {
    const params = new URLSearchParams();
    Object.entries(s.query || {}).forEach(([k, v]) => { if (v) params.set(k, v); });
    nav(`/marketplace?${params.toString()}`);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10" data-testid="saved-searches-page">
      <div className="overline mb-2 inline-flex items-center gap-2"><BookmarkPlus className="w-3 h-3" /> SAVED SEARCHES</div>
      <h1 className="font-display text-4xl tracking-tight font-medium">
        {lang === "ar" ? "بحوثاتي المحفوظة" : "Your saved searches"}
      </h1>
      <p className="text-[#6b6b6b] mt-2">
        {lang === "ar" ? "احفظ عمليات بحثك المتكررة وسنبلغك عند توفر مركبات جديدة." : "Save searches and we'll alert you when new matching vehicles arrive."}
      </p>

      {items.length === 0 ? (
        <div className="surface rounded-3xl p-16 text-center mt-12">
          <BookmarkPlus className="w-12 h-12 mx-auto text-[#8a6f2e] mb-4" />
          <p className="text-[#525252]">
            {lang === "ar" ? "لا توجد عمليات بحث محفوظة. من صفحة السوق اضغط ‘احفظ البحث’." : "No saved searches yet. From the marketplace, tap ‘Save search’."}
          </p>
          <Link to="/marketplace" className="btn-primary mt-6 inline-flex">
            {lang === "ar" ? "تصفح السوق" : "Browse marketplace"} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {items.map((s) => (
            <div key={s.id} className="surface rounded-2xl p-5 " data-testid={`saved-search-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display text-lg">{s.name}</div>
                  <div className="text-xs text-[#6b6b6b] mt-1">
                    {Object.entries(s.query || {}).filter(([_, v]) => v).map(([k, v]) => (
                      <span key={k} className="me-2"><span className="text-[#8a6f2e]">{k}:</span> {v}</span>
                    ))}
                  </div>
                </div>
                <button type="button" onClick={() => remove(s.id)} className="p-2 rounded-lg text-[#6b6b6b] hover:text-red-300" data-testid={`saved-search-delete-${s.id}`} aria-label="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-5 flex items-center justify-between">
                <span className="pill text-[10px]">
                  <Bell className="w-3 h-3 me-1 text-[#8a6f2e]" />
                  {s.result_count} {lang === "ar" ? "نتيجة" : "results"}
                </span>
                <button type="button" onClick={() => run(s)} className="btn-primary text-xs" data-testid={`saved-search-run-${s.id}`}>
                  {lang === "ar" ? "افتح" : "Run"} <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
